import FeedbackSettingsForm from 'components/CreateAssignment/FeedbackSettingsForm';
import FeedbackTypeCard from 'components/CreateAssignment/FeedbackTypeCard';
import { useDialog } from 'contexts/dialog';
import { useSnackbar } from 'contexts/snackbar';
import { omit } from 'lodash';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    ResponsiveDialog,
    Stack,
    styled,
    Text,
} from 'shell/components';
import {
    CUSTOM_CONTENT_WORD_COUNT_LIMIT,
    CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT,
    FEEDBACK_STRUCTURE,
    RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT,
} from 'utils/constants';
import { getHTMLWordCount } from 'utils/helper';

/**
 * Shows feedback structure options that are available in the current product surface.
 * When templated feedback is unavailable, an existing templated value is reset to the
 * rubric structure so the dialog cannot save an unsupported preference.
 */
const FeedbackSettingsDialog = memo(({ isTemplatedFeedbackAvailable = true, open, form: data, onClose, onSave }) => {
    const { setDialog, closeDialog } = useDialog();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');

    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState({ customInstructions: false, templated: {} });

    const handleClose = () => {
        if (form.isDirty) {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit now, any changes you have made to feedback settings will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    onClose();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        } else {
            onClose();
        }
    };

    const handleChange = (changes) => {
        if (typeof changes === 'function') {
            setForm((current) => {
                const newForm = changes(current);
                return {
                    ...newForm,
                    isDirty: true,
                };
            });
        } else {
            setForm((current) => ({
                ...current,
                ...changes,
                isDirty: true,
            }));
        }
    };

    const validateAll = () => {
        if (form.structure === 'rubric' || form.structure === 'system') {
            if (getHTMLWordCount(form.feedbackCustomContext) > CUSTOM_CONTENT_WORD_COUNT_LIMIT) {
                message.error(
                    t(
                        'error.additional-instructions-too-long',
                        'Additional instructions exceeds {{wordCount}} words.',
                        { wordCount: CUSTOM_CONTENT_WORD_COUNT_LIMIT }
                    )
                );
                return false;
            }
            return true;
        }

        if (form.structure === 'templated') {
            if (!data.rubric) {
                message.error(t('error.create-rubric', 'Please create a rubric to use Smart Feedback Templates.'));
                return false;
            }

            let isValid = true;
            data.rubric.criteria.forEach(({ id: criterionId, options }) => {
                const optionIds = options.map(({ id }) => id);
                optionIds.forEach((optionId) => {
                    if (
                        !form.templated?.[criterionId]?.[optionId] ||
                        form.templated?.[criterionId]?.[optionId] > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT
                    ) {
                        isValid = false;
                        setErrors((current) => ({
                            ...current,
                            templated: {
                                ...current.templated,
                                [criterionId]: { ...current.templated?.[criterionId], [optionId]: true },
                            },
                        }));
                        if (!form.templated?.[criterionId]?.[optionId]) {
                            message.error(t('error.missing-required-fields', 'Please complete all required fields.'));
                        }
                        if (form.templated?.[criterionId]?.[optionId] > RUBRIC_OPTION_DESCRIPTION_WORD_COUNT_LIMIT) {
                            message.error(t('error.word-count-exceeded', 'Word count exceeded.'));
                        }
                    } else {
                        setErrors((current) => ({
                            ...current,
                            templated: {
                                ...current.templated,
                                [criterionId]: { ...current.templated?.[criterionId], [optionId]: false },
                            },
                        }));
                    }
                });
            });

            return isValid;
        }

        if (form.structure === 'custom') {
            if (!form.customInstructions.length) {
                setErrors((current) => ({ ...current, customInstructions: true }));
                message.error(
                    t(
                        'error.missing-instructions-custom-feedback',
                        'Please provide instructions for the custom feedback structure.'
                    )
                );
                return false;
            }

            if (getHTMLWordCount(form.customInstructions) > CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT) {
                setErrors((current) => ({ ...current, customInstructions: true }));
                message.error(
                    t('error.custom-instructions-too-long', 'Custom feedback exceeds {{wordCount}} words.', {
                        wordCount: CUSTOM_INSTRUCTIONS_WORD_COUNT_LIMIT,
                    })
                );
                return false;
            }

            setErrors((current) => ({ ...current, customInstructions: false }));
            return true;
        }

        return false;
    };

    const handleClickSave = () => {
        const isValid = validateAll();
        if (!isValid) return;
        onSave(omit(form, ['isDirty']));
        onClose();
    };

    useEffect(() => {
        if (open) {
            setForm({
                ...data.feedbackSettings,
                structure:
                    isTemplatedFeedbackAvailable || data.feedbackSettings.structure !== FEEDBACK_STRUCTURE.TEMPLATED
                        ? data.feedbackSettings.structure
                        : FEEDBACK_STRUCTURE.RUBRIC,
                feedbackCustomContext: data.feedbackCustomContext,
                isDirty: false,
            });
        }
    }, [isTemplatedFeedbackAvailable, open, data.feedbackSettings, data.feedbackCustomContext]);

    if (!form) return null;

    return (
        <CustomResponsiveDialog open={open} scroll={'paper'} onClose={handleClose}>
            <DialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text component={'h1'} variant={'h6'} weight={'medium'}>
                        {t('feedback-section.settings.title', 'Advanced Feedback Settings')}
                    </Text>
                    <IconButton
                        aria-label={'close'}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </DialogTitle>
            <Divider />
            <CustomDialogContent>
                <Stack spacing={5}>
                    <Stack spacing={1}>
                        <Text variant={'p1'}>
                            {t('feedback-section.feedback-structure-label', 'Feedback Structure')}
                        </Text>
                        <HorizontalStack direction={'row'} spacing={5}>
                            <FeedbackTypeCard
                                type={'rubric'}
                                isSelected={form.structure === 'rubric'}
                                onClick={() => handleChange({ structure: 'rubric' })}
                            />
                            <FeedbackTypeCard
                                type={'system'}
                                isSelected={form.structure === 'system'}
                                onClick={() => handleChange({ structure: 'system' })}
                            />
                            {isTemplatedFeedbackAvailable && (
                                <FeedbackTypeCard
                                    type={'templated'}
                                    isSelected={form.structure === 'templated'}
                                    onClick={() => handleChange({ structure: 'templated' })}
                                />
                            )}
                            <FeedbackTypeCard
                                type={'custom'}
                                isSelected={form.structure === 'custom'}
                                onClick={() => handleChange({ structure: 'custom' })}
                            />
                        </HorizontalStack>
                    </Stack>
                    <FeedbackSettingsForm data={data} form={form} errors={errors} handleChange={handleChange} />
                </Stack>
            </CustomDialogContent>
            <Divider />
            <DialogActions>
                <Stack direction={'row'} spacing={4}>
                    <Button aria-hidden={false} color={'neutral'} variant={'tertiary'} onClick={handleClose}>
                        {t('common:controls.cancel', 'Cancel')}
                    </Button>
                    <Button onClick={handleClickSave}>{t('common:controls.save-settings', 'Save Settings')}</Button>
                </Stack>
            </DialogActions>
        </CustomResponsiveDialog>
    );
});

const CustomResponsiveDialog = styled(ResponsiveDialog)(() => {
    return {
        '& .MuiPaper-root > .MuiBox-root': {
            padding: 0,
        },
        '& .MuiDialog-paper': {
            width: '100%',
            height: '100%',
        },
    };
});

const CustomDialogContent = styled(DialogContent)(({ theme }) => {
    return {
        background: theme.palette.ensightful.neutral[50],
    };
});

const HorizontalStack = styled(Stack)(({ theme }) => {
    return {
        margin: theme.spacing(0, -6),
        padding: theme.spacing(2, 6, 4, 6),
        overflowX: 'auto',
    };
});

export default FeedbackSettingsDialog;
